import { SynopticCategory } from '@core/referential/synoptic-category.enum'
import { FlameIcon, HeaterIcon, type LucideIcon, SnowflakeIcon, ThermometerSnowflakeIcon } from 'lucide-react'
import { type PropsWithChildren, createContext, useContext } from 'react'

import { type TranslationKeys } from '@/i18n/translation-shape'

// Domaine d'une catégorie de synoptiques à modules (chaufferie, PAC / groupe froid) : tout ce qui
// distingue les pages d'une catégorie de celles d'une autre. Les pages elles-mêmes sont partagées
// (cf. components/module-synoptic) et lisent ce domaine par contexte ; chaque arbre de routes ne garde
// que de fines enveloppes qui le fournissent.
//
// Les routes sont des littéraux typés du routeur : un domaine ne peut désigner que des routes qui
// existent, et `<Link to={domain.routes.…}>` reste vérifié à la compilation. Les arbres de routes des
// domaines sont jumeaux (mêmes params, même search) : c'est ce qui permet aux pages partagées de
// naviguer sur l'union.
export type ModuleSynopticDomain = {
  category: SynopticCategory
  // Icône de la page et de ses états vides.
  icon: LucideIcon
  // Icône du bouton « Générer un synoptique ».
  generateIcon: LucideIcon
  routes: {
    technical: '/site/$id/boiler-room/technical' | '/site/$id/heat-pump/technical'
    manage: '/site/$id/boiler-room/technical/manage' | '/site/$id/heat-pump/technical/manage'
    generate: '/site/$id/boiler-room/technical/generate' | '/site/$id/heat-pump/technical/generate'
    edit: '/site/$id/boiler-room/technical/manage/$synopticId' | '/site/$id/heat-pump/technical/manage/$synopticId'
  }
  // Libellés qui nomment le domaine (« chaufferie », « PAC / GF ») : le reste des textes est commun aux catégories.
  labels: {
    viewEmpty: TranslationKeys
    viewEmptyHint: TranslationKeys
    manageEmpty: TranslationKeys
    generateBreadcrumb: TranslationKeys
    generateSuccess: TranslationKeys
  }
  // Réglage des points d'un module depuis le synoptique (modale de réglage, loi d'eau comprise).
  // Propre à la chaufferie : sans lui, ni bouton de réglage sur les modules ni requêtes associées.
  moduleSettings: boolean
}

export const BOILER_ROOM_DOMAIN: ModuleSynopticDomain = {
  category: SynopticCategory.BOILER_ROOM,
  icon: HeaterIcon,
  generateIcon: FlameIcon,
  routes: {
    technical: '/site/$id/boiler-room/technical',
    manage: '/site/$id/boiler-room/technical/manage',
    generate: '/site/$id/boiler-room/technical/generate',
    edit: '/site/$id/boiler-room/technical/manage/$synopticId',
  },
  labels: {
    viewEmpty: 'synoptic.boilerRoom.view.empty',
    viewEmptyHint: 'synoptic.boilerRoom.view.emptyHint',
    manageEmpty: 'synoptic.boilerRoom.manage.empty',
    generateBreadcrumb: 'synoptic.boilerRoom.breadcrumb',
    generateSuccess: 'synoptic.boilerRoom.success',
  },
  moduleSettings: true,
}

// PAC / groupe froid : mêmes pages que la chaufferie ; le forçage porte la marche / arrêt et la
// consigne d'eau des machines (cf. boilerRoomSections).
export const HEAT_PUMP_CHILLER_DOMAIN: ModuleSynopticDomain = {
  category: SynopticCategory.HEAT_PUMP_CHILLER,
  icon: ThermometerSnowflakeIcon,
  generateIcon: SnowflakeIcon,
  routes: {
    technical: '/site/$id/heat-pump/technical',
    manage: '/site/$id/heat-pump/technical/manage',
    generate: '/site/$id/heat-pump/technical/generate',
    edit: '/site/$id/heat-pump/technical/manage/$synopticId',
  },
  labels: {
    viewEmpty: 'synoptic.heatPumpChiller.viewEmpty',
    viewEmptyHint: 'synoptic.heatPumpChiller.viewEmptyHint',
    manageEmpty: 'synoptic.heatPumpChiller.manageEmpty',
    generateBreadcrumb: 'synoptic.heatPumpChiller.generateBreadcrumb',
    generateSuccess: 'synoptic.heatPumpChiller.generateSuccess',
  },
  moduleSettings: true,
}

const ModuleSynopticDomainContext = createContext<ModuleSynopticDomain | null>(null)

export function ModuleSynopticDomainProvider({ domain, children }: PropsWithChildren<{ domain: ModuleSynopticDomain }>) {
  return <ModuleSynopticDomainContext.Provider value={domain}>{children}</ModuleSynopticDomainContext.Provider>
}

// Domaine de la page courante. Jette hors d'un provider : une page à modules sans domaine ne saurait
// ni quoi charger ni où naviguer — mieux vaut l'apprendre au premier rendu qu'à la première requête.
export function useModuleSynopticDomain(): ModuleSynopticDomain {
  const domain = useContext(ModuleSynopticDomainContext)
  if (domain === null) throw new Error('useModuleSynopticDomain must be used within a ModuleSynopticDomainProvider')

  return domain
}
